import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild} from '@angular/core';
import {
    checkClipboardPermissions,
    arraysEqual,
    DetectMethodChanges,
    DxValueChanged,
    findHCF,
    isValidNumber,
    isFuturesTicker,
    isVoid,
    makeFuturesTosDesktopLine,
    validateReversingOrders
} from "../../utils";
import {HedgeOrderGroup} from "../data-model/hedge-order-group";
import {HedgePositionGroup} from "../data-model/hedge-position-group";
import {HedgePositionTransaction} from "../data-model/hedge-position-transaction";
import {HedgeStateTransaction} from "../data-model/hedge-state-transaction";
import {MessageBusService} from "../../message-bus.service";
import {filter} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import {
    ComboHighlightedItem,
    ComboHighlightedUIMessage,
    MultiLegOrderDataUIMessage
} from "../../ui-messages/ui-messages";
import {ClipboardService, ETS_CLIPBOARD_LEGS_KEY} from "../../clipboard.service";
import {MarketSide} from "../../trading-model/market-side.enum";
import {DateTime} from "luxon";
import {makeOptionTickerDisplayName, parseOptionTicker} from "../../options-common/options.model";
import {Subscription} from "rxjs";
import {HedgeOrder} from "../data-model/hedge-order";
import {getGroupTotal, getOrderGroupSubTotal, getOrderGroupTotal} from "../hg-totals-calculators";
import {UserSettingsService} from "../../user-settings.service";
import {OrdersRegroupDialogComponent} from "../../orders-regroup-dialog/orders-regroup-dialog.component";
import {OrderToRegroupWrapper} from "../../orders-regroup-dialog/OrderToRegroupWrapper";
import {OrdersGroupToRegroup} from "../../orders-regroup-dialog/OrdersGroupToRegroup";
import {RegroupOrdersConfig} from "../../orders-regroup-dialog/RegroupOrdersConfig";
import {planOptimalArrangement} from "../../orders-regroup-dialog/arrangement-planner";
import {OoaClientService} from "../../orders-regroup-dialog/ooa-client.service";
import {AtmStrikeService} from "../../common-services/atm-strike-service/atm-strike.service";
import {RegroupedOrders} from "../../orders-regroup-dialog/RegroupedOrders";
import {ApgPortfolio} from "../../adjustment-pricing-grid/model/ApgPortfolio";
import {SolutionOrderLegDto} from "../../adjustment-pricing-grid/model/SolutionOrderLegDto";
import {
    GenericConfirmationDialogComponent
} from "../../generic-confirmation-dialog/generic-confirmation-dialog.component";
import {HedgePositionsService} from "../positions-section/hedge-positions/hedge-positions.service";
import {TradingInstrumentDisplayNameService} from "../../trading-instruments/trading-instrument-display-name.service";
import {HgNormalizationService} from "../services/hg-normalization.service";
import {EtsFitToViewportFootprint} from "../../fit-to-viewport/ets-fit-to-viewport.directive";

type Destinations = 'ETS' | 'Hedging Grid' | 'TOS Desktop' | 'TOS Web';

@Component({
    selector: 'ets-hg-transaction-popup',
    templateUrl: 'hg-transaction-popup.component.html',
    styleUrls: [
        './hg-transaction-popup.component.scss',
        '../hedging-grid-common-styles.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class HgTransactionPopupComponent implements OnInit {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _messageBus: MessageBusService,
        private readonly _toastr: ToastrService,
        private readonly _clipboardService: ClipboardService,
        private readonly _userSettingsService: UserSettingsService,
        private readonly _hedgePositionsService: HedgePositionsService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService,
        private readonly _normalizationService: HgNormalizationService,
        private readonly _ooaClient: OoaClientService,
        private readonly _atmStrikeService: AtmStrikeService
    ) {
    }

    private _packageId: string;

    private _isInverted: () => boolean;

    private _subscriptions: Subscription[] = [];

    regroupItems: Array<any> = [{id: 'this', text: 'This Side'}, {id: 'both', text: 'Both Sides'}];

    portfolio: ApgPortfolio;

    @Input() context: 'pkg-cmprsn' | undefined;

    /**
     * OPTIONAL. When set, the Destination / Order To Copy / Copy / Regroup bar of every side is
     * replaced by this one line of text: the dialog opens for LOOKING, not for copying. Maven sets
     * it while another of its cards is applied (CR-20260915-003, owner 2026-09-15: hiding the
     * buttons in the panel kept the trader from seeing what the order offered). Every other caller
     * leaves it null and sees the bar as before.
     */
    @Input() lockHint: string | null = null;

    @ViewChild(OrdersRegroupDialogComponent)
    orderRegroupDialog: OrdersRegroupDialogComponent;

    @ViewChild(GenericConfirmationDialogComponent)
    confirmationDialog: GenericConfirmationDialogComponent;


    visible = false;

    isDoubleMode = false;

    transactions: HedgeStateTransaction[] = [];

    firstSidePositionsVisible = true;

    secondSidePositionsVisible = true;

    portfolioDefaultQty?: number;

    readonly destinations: Destinations[] = [];

    callDestination: Destinations = 'ETS';
    putDestination: Destinations = 'ETS';

    callOrdersToCopyList = [];
    putOrdersToCopyList = [];

    callOrderToCopy: string;
    putOrderToCopy: string;

    getNormFactor = (asset: string): number => {
        if (!asset || !this.portfolio?.asset || asset === this.portfolio.asset) {
            return 1;
        }
        return this._normalizationService.getPointValueRatio(asset, this.portfolio.asset);
    }

    get invertMultiplier(): number {
        return (isVoid(this._isInverted) || !this._isInverted())
            ? 1
            : -1;
    }

    get height() {
        return '95vh';
    }

    get width() {
        return this.transactions.length == 2 ? '99vw' : '50vw';
    }

    get maxWidth() {
        return this.transactions.length === 2 ? '1650' : '820px';
    }

    get maxHeight() {
        return '99vh';
    }

    /**
     * The design footprint of the dialog body, in CSS px, for the fit-to-viewport directive
     * (2026-09-15): the width the fixed pixel columns need per side (two sides + the gap), and
     * the height that used to be the popup's hard `minHeight`. On a viewport smaller than this
     * the body is zoomed down uniformly instead of being clipped - the owner refused scrolling.
     */
    get fitFootprint(): EtsFitToViewportFootprint {
        return {
            width: this.transactions.length === 2 ? 1540 : 770,
            height: 640
        };
    }

    ngOnInit() {

        this.destinations.push('ETS');
        if (this.context === 'pkg-cmprsn') {
            this.destinations.push('Hedging Grid');
        }
        this.destinations.push('TOS Desktop');
        this.destinations.push('TOS Web');

        this._subscriptions.push(
            this._messageBus.of('Hg.RefreshQuotes')
                .pipe(
                    filter(x => this.visible === true)
                )
                .subscribe(x => this._changeDetector.detectChanges())
        );
    }

    @DetectMethodChanges()
    onChange() {
    }

    @DetectMethodChanges()
    show(portfolio: ApgPortfolio, defaultQty: number, transactions: HedgeStateTransaction[], isDoubleMode: boolean, pkgId?: string, isInverted?: () => boolean): void {

        this.portfolio = portfolio;

        this.isDoubleMode = isDoubleMode;

        this._packageId = pkgId;

        this.transactions = transactions;

        if (!isDoubleMode) {
            this.transactions = transactions.filter(x => !isVoid(x.orderTransactions));
        }

        this.portfolioDefaultQty = defaultQty;

        this.fillDropdowns(transactions);

        // A fresh open must re-judge the improvement even for an identical-looking package
        // (CR-20260827-001). The getter recomputes on the next change-detection pass.
        this._improvementFingerprint = null;

        // The backend arrangement's skew rank wants the live underlying price (CR-20260830-002).
        // watch() is idempotent; SPX/SPY/ES are pre-watched by the service anyway.
        if (!isVoid(portfolio?.asset)) {
            this._atmStrikeService.watch(portfolio.asset);
        }

        this._isInverted = isInverted;

        this.visible = true;
    }

    @DetectMethodChanges()
    onClosed() {
        this.visible = false;
    }

    getGroupSubTotal(group: HedgePositionGroup) {
        const total = getGroupTotal(group, this.portfolioDefaultQty, this.getNormFactor);
        return total;
    }

    getGrandTotal(transaction: HedgeStateTransaction) {
        if (transaction.positionTransactions.every(x => x.noChanges)) {
            return undefined;
        }

        const gt = transaction.positionTransactions.map(x => {
            if (x.noChanges) {
                return 0;
            }
            const beforeSum = this.getGroupSubTotal(x.beforeState) || 0;
            const afterSum = this.getGroupSubTotal(x.afterState) || 0;
            return beforeSum + afterSum;
        }).reduce((a, b) => a + b, 0);

        return gt;
    }

    getRowTotal(row: HedgePositionTransaction) {

        if (row.noChanges) {
            return undefined;
        }

        const beforeSum = this.getGroupSubTotal(row.beforeState) || 0;

        const afterSum = this.getGroupSubTotal(row.afterState) || 0;

        const sum = beforeSum + afterSum;

        if (sum === 0) {

            const beforeStatePos = (isVoid(row.beforeState) ? [] : row.beforeState.positions) || [];

            const afterStatePos = (isVoid(row.afterState) ? [] : row.afterState.positions) || [];

            const beforeTickers =
                beforeStatePos.map(x => `${x.qty}${x.expiration}${x.type}${x.strike}`);

            const afterTickers =
                afterStatePos.map(x => `${x.qty}${x.expiration}${x.type}${x.strike}`);

            const areEqual = arraysEqual(beforeTickers, afterTickers);

            if (areEqual) {
                return undefined;
            }
        }

        return sum;
    }

    getOrderGroupTotal(orderGroup: HedgeOrderGroup) {
        const grpTotal = getOrderGroupTotal(orderGroup, this.portfolioDefaultQty, this.getNormFactor);
        return grpTotal;
    }

    getPositionSectionVisibility(side: 'Calls' | 'Puts') {
        if (side === 'Calls') {
            return this.firstSidePositionsVisible;
        }

        if (side === 'Puts') {
            return this.secondSidePositionsVisible;
        }

        return undefined;
    }

    @DetectMethodChanges()
    onPositionSectionVisibilityChanged(side: 'Calls' | 'Puts') {
        if (side === 'Calls') {
            this.firstSidePositionsVisible = !this.firstSidePositionsVisible;
        } else if (side === 'Puts') {
            this.secondSidePositionsVisible = !this.secondSidePositionsVisible;
        }
    }

    getAssetDisplayName(asset: string): string {
        if (!asset) return '';
        return this._displayNameService.getDisplayNameForTicker(asset);
    }

    getPriceClass(price: number) {
        if (!isValidNumber(price, true)) {
            return undefined;
        }

        return price > 0 ? 'credit' : 'debit';
    }

    getDestination(side: 'Calls' | 'Puts') {
        if (side !== 'Calls' && side !== 'Puts') {
            throw Error('Option Type Not Supported');
        }
        return side === 'Calls' ? this.callDestination : this.putDestination;
    }

    getOrdersToCopyList(side: 'Calls' | 'Puts') {
        if (side !== 'Calls' && side !== 'Puts') {
            throw Error('Option Type Not Supported');
        }
        return side === 'Calls' ? this.callOrdersToCopyList : this.putOrdersToCopyList;
    }

    getOrderToCopy(side: 'Calls' | 'Puts') {
        return side === 'Calls' ? this.callOrderToCopy : this.putOrderToCopy;
    }

    async onCopyClicked(side: 'Calls' | 'Puts') {

        if (side !== 'Calls' && side !== 'Puts') {
            throw Error(`Option Type "${side || 'N/A'}" Not Supported`);
        }


        const hedgePositions = await this._hedgePositionsService.getHedgePositions(this.portfolio);
        const orderGroups = this.getOrderGroupsToCopy(side);
        let orders = orderGroups.flatMap(x => x.balancedOrders)
            .flatMap(x => x);

        try {
            await validateReversingOrders(orders, hedgePositions, this._toastr, this.confirmationDialog);
        } catch {
            return;
        }


        const destination = this.getDestination(side);

        try {
            if (destination === 'ETS') {
                this.copyOrdersToEts(side);
            } else if (destination === 'Hedging Grid') {
                this.copySideToHedgingGrid(side);
            } else if (destination === 'TOS Desktop') {
                const tosOk = await this.copyOrdersToTOSDesktop(side);
                if (!tosOk) { return; }
            } else if (destination === 'TOS Web') {
                await this.copyOrdersToTOSWeb(side);
            } else {
                this._toastr.error('Unknown Destination');
                return;
            }

            if (destination !== 'Hedging Grid') {
                this.saveAfterState(side);
            }
        } catch (e) {
            console.error(e);
            this._toastr.error('Could not copy to clipboard. Please check browser permissions, or contact system administrator');
        }
    }

    @DetectMethodChanges()
    @DetectMethodChanges()
    onOrderToCopySelected(args: DxValueChanged<string>, side: 'Calls' | 'Puts') {
        if (side === 'Calls') {
            this.callOrderToCopy = args.value;
        } else if (side === 'Puts') {
            this.putOrderToCopy = args.value;
        }

        // A narrowed selection is a different package; the decorator's change-detection pass
        // makes the `improvementAvailable` getter re-judge it (CR-20260827-001). The other
        // side's selector is untouched by design.
    }

    @DetectMethodChanges()
    onDestinationSelected(args: DxValueChanged<Destinations>, side: 'Calls' | 'Puts') {

        if (isVoid(side)) {
            return;
        }

        if (side === 'Calls') {
            this.callDestination = args.value;
        } else if (side === 'Puts') {
            this.putDestination = args.value;
        }
        const key = `hg.last-used-destination.${side.toLowerCase()}`;
        this._userSettingsService.setValue(key, args.value);
    }

    getOrderPackageTotal(orderPkg: HedgeOrder[]) {
        const subTotal = getOrderGroupSubTotal(orderPkg, undefined, this.getNormFactor);
        return subTotal;
    }

    @DetectMethodChanges()
    copyToHedgingGrid() {

        const data: ComboHighlightedUIMessage[] = [];

        for (let transaction of this.transactions) {

            const positions = transaction.positionTransactions
                .filter(pt => !pt.noChanges)
                .flatMap(pt => {
                    return pt.afterState.positions;
                });


            const items = positions.map(x => {

                const qty = x.qty * this.invertMultiplier;

                const item: ComboHighlightedItem = {
                    netPosition: Math.abs(qty),
                    side: qty > 0 ? MarketSide.Buy : (qty < 0 ? MarketSide.Sell : MarketSide.Flat),
                    ticker: x.ticker,
                    underlying: x.asset
                } as any;
                return item;
            });

            const msg: ComboHighlightedUIMessage = {
                strategyName: positions[0].label,
                items
            };

            data.push(msg);
        }

        this._clipboardService.put('hg.pkg-cmprsn', data);

        this._toastr.success('Hedges Have Been Copied to Clipboard!');
    }

    @DetectMethodChanges()
    copySideToHedgingGrid(side: 'Calls' | 'Puts') {

        if (isVoid(this.transactions)) {
            return;
        }

        const hedgeStateTransaction = this.transactions.find(x => x.side === side);

        if (isVoid(hedgeStateTransaction)) {
            return;
        }

        const positions = hedgeStateTransaction.positionTransactions
            .filter(pt => !pt.noChanges)
            .flatMap(pt => {
                return pt.afterState.positions;
            });


        const items = positions.map(x => {
            const item: ComboHighlightedItem = {
                netPosition: Math.abs(x.qty),
                side: x.qty > 0 ? MarketSide.Buy : (x.qty < 0 ? MarketSide.Sell : MarketSide.Flat),
                ticker: x.ticker,
                underlying: x.asset
            } as any;
            return item;
        });

        const msg: ComboHighlightedUIMessage = {
            strategyName: positions[0].label,
            items
        };

        this._clipboardService.put('hg.pkg-cmprsn', [msg]);

        this._toastr.success(`"${hedgeStateTransaction.side}" Hedges Have Been Copied to Clipboard!`);
    }

    getOrderActionText(text: string): string {
        if (isVoid(text)) {
            return text;
        }

        if (isVoid(this._isInverted) || !this._isInverted()) {
            return text;
        }

        if (text.indexOf('Buy') !== -1) {
            text = text.replace('Buy', 'Sell');
        } else if (text.indexOf('Sell') !== -1) {
            text = text.replace('Sell', 'Buy');
        }

        return text;
    }

    private saveAfterState(side: "Calls" | "Puts") {
        const sideToCopy = this.transactions.find(x => x.side === side);

        let slot: 'first' | 'second' = 'first';

        if (this.isDoubleMode) {
            if (side === 'Puts') {
                slot = 'second';
            }
        }

        const hedgePositionGroups = sideToCopy
            .positionTransactions
            .map(x => x.afterState);

        const positions = hedgePositionGroups
            .filter(x => !isVoid(x))
            .flatMap(x => x.positions);


        this._messageBus.publish({
            topic: 'Hg.SaveAfterState',
            payload: {
                positions,
                slot,
                packageId: this._packageId,
                portfolio: this.portfolio
            }
        });

        this._toastr.success('Orders Copied to Clipboard');
    }

    private fillDropdowns(transactions: HedgeStateTransaction[]) {

        this.callOrdersToCopyList = [];
        this.putOrdersToCopyList = [];

        transactions.forEach(trns => {
            const arr = trns.side === 'Calls' ? this.callOrdersToCopyList : this.putOrdersToCopyList;
            const labels = trns.orderTransactions.map(x => x.label);

            if (labels.length > 0) {
                arr.push('All');
                arr.push(...labels);
            }
        });

        if (this.callOrdersToCopyList.length > 0) {
            this.callOrderToCopy = this.callOrdersToCopyList[0];
        }

        if (this.putOrdersToCopyList.length > 0) {
            this.putOrderToCopy = this.putOrdersToCopyList[0];
        }

        let callKey = `hg.last-used-destination.calls`;
        let putKey = `hg.last-used-destination.puts`;

        const cDest = this._userSettingsService.getValue(callKey);
        const pDest = this._userSettingsService.getValue(putKey);

        this.callDestination = cDest as any;
        this.putDestination = pDest as any;
    }

    private copyOrdersToEts(side: "Calls" | "Puts", regrouped?: RegroupedOrders[]) {

        let orders: HedgeOrder[][] = [];

        if (regrouped) {
            orders = regrouped.map(x => x.orders as HedgeOrder[]);
        } else {
            const orderGroups = this.getOrderGroupsToCopy(side);
            orders = orderGroups.flatMap(x => x.balancedOrders);
        }

        const orderLegsGroups = orders.map(grp => {

            const mappedOrders = grp.map(o => {
                const marketSide = o.action.indexOf('Buy') >= 0 ? MarketSide.Buy : MarketSide.Sell;
                const item: ComboHighlightedItem = {
                    ticker: o.ticker,
                    underlying: o.asset,
                    side: marketSide,
                    itemType: null,
                    netPosition: o.qty
                } as any;

                return item;
            });

            const comboMsg: ComboHighlightedUIMessage = {
                items: mappedOrders,
                orderParams: {
                    orderQty: 1
                }
            }

            return comboMsg;
        });

        if (orderLegsGroups.length > 1) {

            const main = orderLegsGroups[0];
            const linked = orderLegsGroups[1];
            const secondLinked = orderLegsGroups[2];
            const thirdLinked = orderLegsGroups[3];

            const msg: MultiLegOrderDataUIMessage = {
                main,
                linked,
                secondLinked,
                thirdLinked,
                linkType: 'OTO',
                cashFlowSpec: {} as any,
            };

            this._clipboardService.put('multi-combo', msg);

        } else {

            const msg: ComboHighlightedUIMessage = {
                items: orderLegsGroups[0].items,
                orderParams: {
                    orderQty: 1
                }
            };

            this._clipboardService.put('combo', msg);
        }

        // CR-20260917-002: "ETS" also means "Copy To Other Panel" (owner ruling 2026-09-17). The
        // legacy keys above feed the Multi-Trade Pad only; every other panel's paste (OPG, OPP,
        // Hedge Pad, Hedge Matrix) reads the universal key, so the same orders go there too — the
        // dual write the adjustment solution pop-up already does through CopyOrdersToService.
        // ⚠ The universal convention is `netPosition` = MAGNITUDE with the direction in `side`
        // (readers do `netPosition * side`); the legacy items above carry the SIGNED order qty,
        // so they are re-shaped here rather than reused, or every Sell leg would paste as a Buy.
        const universal: ComboHighlightedUIMessage[] = orderLegsGroups.map(grp => ({
            items: grp.items.map(i => ({...i, netPosition: Math.abs(i.netPosition)})),
            orderParams: {
                orderQty: 1
            }
        }));

        this._clipboardService.put(ETS_CLIPBOARD_LEGS_KEY, universal.length > 1 ? universal : universal[0]);
        console.log(`[ETS-CLIPBOARD] write source=hg-transaction-popup via=copyOrdersToEts side=${side} orders=${universal.length} legs=${universal.reduce((n, m) => n + m.items.length, 0)}`);
    }

    private async copyOrdersToTOSDesktop(side: 'Calls' | 'Puts', regrouped?: RegroupedOrders[]) {

        let orders;

        if (regrouped) {
            orders = regrouped.map(x => {
                return {
                    orders: x.orders as HedgeOrder[],
                    limitPrice: x.limitPrice
                }
            });
        } else {

            console.assert(side === 'Calls' || side === 'Puts', 'Wrong side');

            const ordersToCopy = this.getOrderGroupsToCopy(side);

            orders = ordersToCopy
                .flatMap(x => x.balancedOrders)
                .map(x => {
                    return {
                        orders: x,
                        limitPrice: null
                    }
                });
        }

        const lines = orders
            .map(x => this.makeTosDesktopOrderLine(x));

        if (lines.some(l => isVoid(l))) {
            return false;
        }

        for (let i = lines.length - 1; i >= 0; i--) {
            if (i === 0) {
                break;
            }
            let line = lines[i];
            line += ' TRG BY';
            lines[i] = line;
        }

        const text = lines.join('\n');

        // @ts-ignore
        const result = await checkClipboardPermissions();

        if (result) {
            window.focus();
            await navigator.clipboard.writeText(text);
        } else {
            throw Error('Could not copy to clipboard. Please check browser permissions');
        }

        return true;
    }

    private makeTosDesktopOrderLine(args: { orders: HedgeOrder[], limitPrice?: number }): string {

        if (isVoid(args?.orders)) {
            return undefined;
        }

        const orders = args.orders;
        const underlying = orders[0].asset;

        const futuresOrders = orders.filter(o => isFuturesTicker(o.asset));
        if (futuresOrders.length > 0 && futuresOrders.length !== orders.length) {
            this._toastr.error('Mixed futures and equity legs are not supported in a single TOS order');
            return undefined;
        }

        if (futuresOrders.length === orders.length) {
            return this.makeFuturesTosDesktopOrderLine(args);
        }

        return this.makeEquityTosDesktopOrderLine(args);
    }

    private makeEquityTosDesktopOrderLine(args: { orders: HedgeOrder[], limitPrice?: number }): string {

        const orders = args.orders;
        const limitPrice = args.limitPrice;

        const transformedLegGroups = orders.map(order => ({
            qty: order.qty,
            date: DateTime.fromFormat(order.expiration, 'yyyy-MM-dd').toFormat('dd MMM yy').toUpperCase(),
            strike: order.strike,
            type: order.type.toUpperCase(),
            price: order.price
        }));

        const underlying = orders[0].asset;

        const quantities = transformedLegGroups.map(x => x.qty).join('/');
        const dates = transformedLegGroups.map(x => x.date).join('/');
        const strikes = transformedLegGroups.map(x => x.strike).join('/');
        const types = transformedLegGroups.map(x => x.type).join('/');

        const comboPrice = transformedLegGroups.reduce((p, c) => p + c.price, 0) * -1;
        if (!isValidNumber(comboPrice)) {
            return undefined;
        }

        const px = isValidNumber(limitPrice) ? Math.abs(limitPrice) + '' : '';

        if (underlying === 'SPX') {
            return `BUY 1 ${quantities} CUSTOM ${underlying} 100 (Weeklys) ${dates} ${strikes} ${types} @${px} LMT GTC`;
        }
        return `BUY 1 ${quantities} CUSTOM ${underlying} 100 ${dates} ${strikes} ${types} @${px} LMT GTC`;
    }

    private makeFuturesTosDesktopOrderLine(args: { orders: HedgeOrder[], limitPrice?: number }): string {

        const orders = args.orders;
        const limitPrice = args.limitPrice;

        const uniqueExpirations = new Set(orders.map(o => o.expiration));
        if (uniqueExpirations.size > 1) {
            this._toastr.error('Multi-expiration combos are not supported yet for ES options in TOS Desktop format');
            return undefined;
        }

        const hcf = findHCF(orders.map(o => Math.abs(o.qty)));
        const totalQty = hcf;

        const transformedLegGroups = orders.map(order => ({
            qty: order.qty / hcf,
            date: DateTime.fromFormat(order.expiration, 'yyyy-MM-dd').toFormat('d MMM yy').toUpperCase(),
            strike: order.strike,
            type: order.type.toUpperCase(),
            price: order.price
        }));

        const comboPrice = transformedLegGroups.reduce((p, c) => p + c.price, 0) * -1;
        if (!isValidNumber(comboPrice)) {
            return undefined;
        }

        const underlying = orders[0].asset;
        const expDate = DateTime.fromFormat(orders[0].expiration, 'yyyy-MM-dd');
        const px = isValidNumber(limitPrice) ? Math.abs(limitPrice) + '' : '';

        return makeFuturesTosDesktopLine({
            expDate,
            underlying,
            strikes: transformedLegGroups.map(x => x.strike),
            types: transformedLegGroups.map(x => x.type),
            legQtys: transformedLegGroups.map(x => x.qty),
            totalQty,
            dates: transformedLegGroups.map(x => x.date),
            price: px
        });
    }

    private getOrderGroupsToCopy(side: 'Calls' | 'Puts'): HedgeOrderGroup[] {

        if (side !== 'Calls' && side !== 'Puts') {
            throw new Error('Wrong side');
        }

        const trans = this.transactions.find(x => x.side === side);

        const orderToCopy = side === 'Calls' ? this.callOrderToCopy : this.putOrderToCopy;

        const orderGroups = orderToCopy === 'All'
            ? trans.orderTransactions
            : [trans.orderTransactions.find(x => x.label === orderToCopy)];

        if (isVoid(orderGroups)) {
            throw new Error(`Cannot find order group: ${orderToCopy} in ${side}`);
        }

        return orderGroups;
    }

    private async copyOrdersToTOSWeb(side: 'Calls' | 'Puts') {

        const orderGroupsToCopy = this.getOrderGroupsToCopy(side);

        if (orderGroupsToCopy.length === 0) {
            throw new Error('No Orders To Copy');
        }

        if (orderGroupsToCopy.length > 1) {
            throw new Error('TOS Web Only Accepts one Order at a Time');
        }

        const group = orderGroupsToCopy[0];

        const symbol = `symbol=${group.orders[0].asset}`;

        const tosLeg = group.orders.map((leg, ix) => {
            const tosLegNo = `leg${ix + 1}`;
            const ot = parseOptionTicker(leg.ticker);
            const expiration = DateTime.fromFormat(ot.expiration, 'yyyy-MM-dd').toFormat('yyMMdd');
            const tosTicker = `.${ot.expirationTicker.substring(1)}${expiration}${ot.type[0].toUpperCase()}${ot.strike}`;
            const actionSign = leg.action.indexOf('Buy') >= 0 ? 1 : -1;
            const fullLegDescriptor = `${tosLegNo}=${leg.qty},${tosTicker}`;

            return fullLegDescriptor;
        });

        const oType = 'orderType=LIMIT';
        const oPrice = 'limitPrice=';
        const oTif = 'orderTif=GTC';

        const host = 'https://trade.thinkorswim.com/trade';

        const request = `${host}?${symbol}&${tosLeg.join('&')}&${oType}&${oPrice}&${oTif}`;

        await navigator.clipboard.writeText(request);

        return true;
    }

    async onRegroupClicked(event: any, side?: 'Calls' | 'Puts') {
        if (event.itemData.id === 'this') {
            await this.showRegroupDialog([side])
        } else if (event.itemData.id === 'both') {
            await this.showRegroupDialog(['Calls', 'Puts']);
        }
    }

    async showRegroupDialog(sides: ('Calls' | 'Puts')[]) {

        const destination = this.getDestination(sides[0]);

        if (isVoid(destination)) {
            this._toastr.error('Unsupported copy destination');
            return;
        }

        const groups = this.buildRegroupGroups(sides[0]);

        const groups2 = sides.length > 1
            ? this.buildRegroupGroups(sides[1])
            : [];

        const cfg: RegroupOrdersConfig = {
            orders: groups,
            orders2: groups2,
            // CR-20260830-002: the arrangement runs EVERY time inside the dialog, against the
            // dialog's OWN rows. The probe's result is not passed through (its plan references
            // the probe's wrappers, not the dialog's - CR-20260830-003); the probe has warmed
            // the OoaClientService cache, which is what makes the open instant.
            atm: this.currentAtm ?? undefined,
            // The Hedging Grid is the ONLY caller that auto-arranges on open (owner, 2026-09-18).
            autoArrangeOnOpen: true,
            copyCallback: async (args: RegroupedOrders[]) => {
                try {

                    const side = undefined;

                    if (destination === 'ETS') {
                        this.copyOrdersToEts(side, args);
                    } else if (destination === 'TOS Desktop') {
                        const tosOk = await this.copyOrdersToTOSDesktop(side, args);
                        if (!tosOk) { return; }
                    } else {
                        this._toastr.error('Not Supported Destination Selected');
                    }

                    sides.forEach(s => this.saveAfterState(s));

                } catch {
                    this._toastr.error('"Copy Orders" Operation Completed With Errors');
                }
            },
            portfolio: this.portfolio,
            context: 'hg',
            reverseOrderValidator: async (orders: (SolutionOrderLegDto | HedgeOrder)[]) => {
                const positions = await this._hedgePositionsService.getHedgePositions(this.portfolio);
                return validateReversingOrders(orders,
                    positions,
                    this._toastr,
                    this.confirmationDialog);
            }
        };

        await this.orderRegroupDialog.show(cfg)
    }

    getRegroupOptions(side: 'Calls' | 'Puts') {

        if (side !== 'Calls' && side !== 'Puts') {
            return [];
        }

        const options = [];
        options.push({
            id: 'this',
            text: side,
        });

        options.push({
            id: 'both',
            text: 'Calls & Puts',
        });
        return options;

    }

    // -------------------------------------------------------------------------------------
    // EARLY IMPROVEMENT AVAILABILITY (CR-20260827-001; backend-served since CR-20260830-002)
    //
    // "Improvements should not be counted by the side... it's still an improvement of the whole
    // package" (owner). One verdict, judged over EXACTLY the orders the Re-group dialog would
    // receive. The judgement is a BACKEND round trip now: while it is in flight the Regroup
    // button carries its own inner spinner (the popup itself stays fully accessible - owner
    // ruling), then glows ORANGE when a different arrangement exists, RED when that arrangement
    // is degraded (naked residue - the run is presented as failed), or stays plain on
    // "Unchanged". The button itself is NEVER disabled (owner: "don't block it, no matter
    // what") - clicking during the probe just opens the dialog, which runs its own request.
    //
    // ⚠⚠ JUDGED FROM A FINGERPRINT, NOT AT show() TIME. A one-shot verdict at open is a verdict
    // on unsettled data (CR-20260827-001 round-1 smoke failure). The getter re-fingerprints the
    // actual input on every change-detection pass (labels + legs + the two Order To Copy
    // selections + the ATM - cheap string work) and launches a probe ONLY when the fingerprint
    // moves: popup open, orders arriving, a dropdown narrowing, the ATM stepping. A reply is
    // applied only if its fingerprint is still the current one - late answers to a changed
    // package are dropped.
    // -------------------------------------------------------------------------------------

    private _improvementFingerprint: string = null;

    private _improvementAvailable = false;

    /** 'probing' renders the inner spinner; 'improvement' orange; 'degraded' red. */
    ooaState: 'idle' | 'probing' | 'improvement' | 'degraded' | 'none' = 'idle';

    /** The sides that actually hold order groups - what an orange click opens. */
    private _sidesWithOrders: ('Calls' | 'Puts')[] = [];

    get improvementAvailable(): boolean {

        const fingerprint = this.improvementFingerprint();

        if (fingerprint !== this._improvementFingerprint) {
            this._improvementFingerprint = fingerprint;
            this.judgeImprovement();
        }

        return this._improvementAvailable;
    }

    get ooaProbing(): boolean {
        return this.ooaState === 'probing';
    }

    get ooaDegraded(): boolean {
        return this.ooaState === 'degraded';
    }

    private get currentAtm(): number | null {
        const atm = this._atmStrikeService.getCurrentAtm(this.portfolio?.asset);
        return isValidNumber(atm) ? atm : null;
    }

    /**
     * Everything the verdict depends on, as one string. A group whose `orders` have not arrived
     * yet prints as `pending`, so their arrival changes the fingerprint and re-triggers the
     * judgement - that is the whole point of fingerprinting instead of computing once.
     */
    private improvementFingerprint(): string {

        return (this.transactions || [])
            .map(t => {
                const groups = (t.orderTransactions || [])
                    .map(g => isVoid(g?.orders)
                        ? `${g?.label}:pending`
                        : `${g.label}:` + g.orders.map(o => `${o.ticker}x${o.qty}`).join(','))
                    .join('|');
                const orderToCopy = t.side === 'Calls' ? this.callOrderToCopy : this.putOrderToCopy;
                return `${t.side}[${orderToCopy}]{${groups}}`;
            })
            .join(';') + `|atm=${this.currentAtm}`;
    }

    private judgeImprovement() {

        try {

            this._sidesWithOrders = (this.transactions || [])
                .filter(t => !isVoid(t.orderTransactions)
                    && t.orderTransactions.every(g => !isVoid(g?.orders)))
                .map(t => t.side);

            if (this._sidesWithOrders.length === 0) {
                this._improvementAvailable = false;
                this.ooaState = 'idle';
                console.log('[regroup-improvement] improves=false (no side has settled orders yet)');
                return;
            }

            const groups = this.buildRegroupGroups(this._sidesWithOrders[0]);

            const groups2 = this._sidesWithOrders.length > 1
                ? this.buildRegroupGroups(this._sidesWithOrders[1])
                : [];

            // Fire-and-forget: the getter cannot await. The spinner state renders now; the
            // verdict lands via detectChanges() when the reply arrives.
            this.ooaState = 'probing';
            this.probeArrangement(this._improvementFingerprint, groups, groups2);

        } catch (e) {
            // No availability is an OFFER withheld, never an error surfaced: the popup and the
            // manual Regroup path must keep working exactly as before this feature existed.
            console.error('[regroup-improvement] availability check failed - no offer made', e);
            this._improvementAvailable = false;
            this.ooaState = 'none';
        }
    }

    private async probeArrangement(fingerprint: string,
                                   groups: OrdersGroupToRegroup[],
                                   groups2: OrdersGroupToRegroup[]): Promise<void> {

        try {
            const result = await planOptimalArrangement(groups, groups2, this._ooaClient, this.currentAtm);

            if (fingerprint !== this._improvementFingerprint) {
                // The package changed while the probe was in flight - a newer probe owns the
                // verdict; this answer is stale and must not light anything.
                return;
            }

            if (result.status === 'ok' && result.pages.length > 0) {
                this._improvementAvailable = true;
                this.ooaState = result.pages[0].degraded ? 'degraded' : 'improvement';
            } else {
                this._improvementAvailable = false;
                this.ooaState = 'none';
            }

            // Log the VALUE and the input, not just the class - a wrong verdict must be
            // disputable from the console alone.
            const describe = (gs: OrdersGroupToRegroup[]) => gs
                .map(g => `${g.title}(${g.orders.length})`).join('+') || '(none)';
            console.log(`[regroup-improvement] improves=${this._improvementAvailable}` +
                ` state=${this.ooaState}` +
                ` requestId=${result.requestId || 'n/a'}` +
                ` sides=${this._sidesWithOrders.join(',')}` +
                ` input=${describe(groups)}${groups2.length ? ' / ' + describe(groups2) : ''}`);

        } catch (e) {
            console.error('[regroup-improvement] availability check failed - no offer made', e);

            if (fingerprint === this._improvementFingerprint) {
                this._improvementAvailable = false;
                this.ooaState = 'none';
            }
        } finally {
            // The message-bus reply lands outside NgZone; OnPush needs the explicit nudge.
            this._changeDetector.detectChanges();
        }
    }

    /**
     * The orange click. What opens "depends on what was calculated" (owner): both sides when
     * both hold orders, the one side that does otherwise. The menu is skipped either way.
     */
    async onImprovedRegroupClicked() {
        await this.showRegroupDialog(this._sidesWithOrders);
    }

    private buildRegroupGroups(side: 'Calls' | 'Puts'): OrdersGroupToRegroup[] {

        return this.getOrderGroupsToCopy(side).map(hog => {

            const wrappers = hog.orders.map(o => new OrderToRegroupWrapper(o, hog.color));

            return new OrdersGroupToRegroup(hog.label as any, wrappers);
        });
    }
}
